/** @jsxImportSource @emotion/react */
'use client';

import React, { useEffect } from 'react';
import { AlertTriangle, CheckCircle, XCircle, Info, X } from 'lucide-react';
import {
  DismissButton,
  IconWrapper,
  StyledToast,
  ToastMessage,
} from './styled';
import type { ToastProps, ToastVariant } from './types';
import { defaultIconProps } from '../Icon';

const ICON_MAP: Record<ToastVariant, typeof CheckCircle> = {
  success: CheckCircle,
  error: XCircle,
  info: Info,
  warning: AlertTriangle,
};

const DEFAULT_AUTO_DISMISS_MS = 5000;

export const Toast = React.forwardRef<HTMLDivElement, ToastProps>(
  (
    {
      children,
      variant = 'success',
      onDismiss,
      autoDismissMs,
      position = 'top-right',
      embedded = false,
      className,
      testId = 'toast',
      css,
      style,
      ...rest
    },
    ref
  ) => {
    // Error → 0 ms means "no auto-dismiss timer" (persist until the user
    // dismisses). WCAG 2.2.1: error messages must not disappear on a timeout.
    // `??` still lets callers pass an explicit positive autoDismissMs to override.
    const resolvedAutoDismissMs =
      autoDismissMs ?? (variant === 'error' ? 0 : DEFAULT_AUTO_DISMISS_MS);

    useEffect(() => {
      if (!onDismiss || !resolvedAutoDismissMs || resolvedAutoDismissMs <= 0) {
        return;
      }
      const timer = setTimeout(onDismiss, resolvedAutoDismissMs);
      return () => clearTimeout(timer);
    }, [onDismiss, resolvedAutoDismissMs]);

    const VariantIcon = ICON_MAP[variant];
    const isError = variant === 'error';

    return (
      <StyledToast
        ref={ref}
        role={isError ? 'alert' : 'status'}
        aria-live={isError ? 'assertive' : 'polite'}
        data-testid={testId}
        data-variant={variant}
        data-position={embedded ? 'embedded' : position}
        $variant={variant}
        $position={position}
        $embedded={embedded}
        className={className}
        css={css}
        style={style}
        {...rest}
      >
        <IconWrapper $variant={variant} aria-hidden="true">
          <VariantIcon {...defaultIconProps} size={20} />
        </IconWrapper>
        <ToastMessage>{children}</ToastMessage>
        {onDismiss ? (
          <DismissButton
            type="button"
            onClick={onDismiss}
            aria-label="Dismiss notification"
          >
            <X {...defaultIconProps} size={16} />
          </DismissButton>
        ) : null}
      </StyledToast>
    );
  }
);

Toast.displayName = 'Toast';
